import { selectLanguage } from '@ff-os/pdp-shared-redux/slices/globalSlice';
import { Pdp } from '@ff-os/pdp-shared-registry/Pdp';
import { createPipeline } from '@ff-os/rendering-react/createPipeline';
import { drawLoadingPlaceholder } from '@ff-os/rendering-react/placeholder/drawLoadingPlaceholder';
import { createTransform, Spaces } from '@ff-os/rendering-shared/coordinates';
import { toBlob } from '@ff-os/shared-react/dom/images/toBlob';
import { maintainAspectRatioOf } from '@ff-os/shared-utils/aspect-ratios';
import { selectBorderById } from '@ff-scene-plugins/photo-prints-redux/selectors/borderSelectors';
import { selectSizeById } from '@ff-scene-plugins/photo-prints-redux/selectors/sizeSelectors';
import { selectGroupById } from '@ff-scene-plugins/photo-prints-redux/slices/groupsSlice';
import { PhotoPrintsPdpState } from '@ff-scene-plugins/photo-prints-redux/store';
import { getRotationSettings, RotationSettings } from '@ff-scene-plugins/photo-prints-shared/rotationSettings';
import { toPaintable } from '@ff-scene-plugins/photo-prints-shared/toPaintable';
import { toSpaces } from '@ff-scene-plugins/photo-prints-shared/toSpaces';
import { createDOMCanvas, Paintable, Size } from 'ff-canvas';
import { noop } from 'lodash';
import { selectFilledPhotoById } from './_redux-photos';
import { selectVariantByPhotoId } from './_redux-variants';

export const renderProjectToThumbnail = async (
    state: PhotoPrintsPdpState,
    targetSize: Size,
    options: Pdp.Scene.RenderThumbnailOptions
): Promise<Pdp.Scene.RenderThumbnailResult> => {

    const photoId = state.project.photos.ids[0];

    let thumbnail: HTMLCanvasElement;

    if (!photoId) {
        thumbnail = await renderEmptyPhoto(targetSize);
    } else {

        const language = selectLanguage(state);
        const photo = selectFilledPhotoById(state, photoId);
        const variant = selectVariantByPhotoId(state, photoId);
        const variantSize = selectSizeById(state, variant.sizeId)!;
        const border = selectBorderById(state,variant.borderId);
        const group = selectGroupById(state, photo.groupId);
        const rotationSettings = getRotationSettings(variantSize, photo);
        const outputSize = getOutputSize(variantSize, targetSize, rotationSettings);
        const paintables = [toPaintable(photo, variantSize, state.auth.jwt, 'mockup', border?.value ? {
            // Double the border width to apply the same size on all sides
            size: variantSize.borderWidth * 2,
            color: border.color ?? '#FFF'
        } : undefined, group)];
        thumbnail = await render(paintables, outputSize, toSpaces(variantSize, photo), language);
    }

    return {
        toBlob: async () => await toBlob(thumbnail),
        toBase64: async () => thumbnail.toDataURL()
    };
};

const getOutputSize = (
    variantSize: Size,
    targetSize: Size,
    rotationSettings: RotationSettings
) => {

    const rotatedSize = {
        width: rotationSettings.rotateScene ? variantSize.height : variantSize.width,
        height: rotationSettings.rotateScene ? variantSize.width : variantSize.height
    };

    return maintainAspectRatioOf(rotatedSize, targetSize);
};

const render = async (
    paintables: Paintable[],
    outputSize: Size,
    spaces: Spaces,
    language: string
) => {

    const canvas = createDOMCanvas();
    canvas.width = outputSize.width;
    canvas.height = outputSize.height;

    const pipeline = createPipeline(canvas, { t: a => a, language }, {
        requestRender: noop,
        transform: createTransform(outputSize.width, outputSize.height, spaces),
        useDevicePixelRatio: false
    });

    await pipeline.preloadResources(paintables);
    pipeline.paintAll(paintables);

    return canvas;
};

const renderEmptyPhoto = async (outputSize: Size) => {

    const canvas = createDOMCanvas();
    canvas.width = outputSize.width;
    canvas.height = outputSize.height;

    const ctx = canvas.getContext('2d')!;
    ctx.fillStyle = 'rgba(0, 0, 0, 0.2)';
    drawLoadingPlaceholder(ctx, outputSize.width, outputSize.height);

    return canvas;
};